/**
 * nav-model — DECLARATIVE model of the sidebar navigation by role/capability.
 *
 * Each item defines `visible(ctx)` over a pure `NavContext` (no hooks), so
 * the visibility matrix is testable in vitest without a DOM. The context is
 * built reactively in `use-nav-context.ts` (per-tab session + active org's
 * role + local-first guardian/teacher), so an account or org switch
 * re-evaluates visibility instantly.
 *
 * UX ONLY: the real authority is still the route guards and the backend
 * (write-path / admin middleware). Hiding an item isn't security; sparing
 * the user a 403/redirect is just courtesy.
 */
import type { Component } from "solid-js";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import {
  Banknote,
  BarChart3,
  BookMarked,
  BookOpen,
  Building2,
  CalendarDays,
  ClipboardCheck,
  ClipboardList,
  Contact,
  CreditCard,
  FileSpreadsheet,
  GraduationCap,
  History,
  IdCard,
  LifeBuoy,
  Megaphone,
  Route as RouteIcon,
  School,
  ShieldCheck,
  Sparkles,
  Trash2,
  Trophy,
  User,
  Users,
  UsersRound,
} from "@fleet/icons";
import { isAdminCapable, MODALITY_IN_PERSON_KEY, type OrgRole, roleCan } from "@space/shared";

/** Pure visibility context (built reactively by `useNavContext`). */
export interface NavContext {
  /** REAL per-tab account (an explicit guest is never "logged"). */
  isLoggedIn: boolean;
  /** Platform role (`session.user.role`) — F2-C capabilities. */
  platformRole: string | null;
  /** Normalized role in the ACTIVE org (null when there's no org/membership). */
  orgRole: OrgRole | null;
  /** Has own cohort_teacher rows (or teacher role in the active org). */
  isTeacher: boolean;
  /** Has own synced guardian_link rows (is a guardian). */
  isGuardian: boolean;
  /**
   * FLAGS: is the feature effectively enabled (graph included) for the
   * active org? Built by `useNavContext` with `usePlatformFlags` + synced
   * org_settings; in tests `() => true` suffices.
   */
  featureEnabled: (key: string) => boolean;
}

export interface NavItem {
  to: string;
  label: MessageDescriptor;
  icon: Component<{ class?: string }>;
  testid: string;
  visible: (ctx: NavContext) => boolean;
  /**
   * FLAGS: feature gating the item (in addition to role). Disabled for the
   * active org ⇒ the item disappears from the bar (the route shows its
   * "disabled by your academy" state if navigated to directly).
   */
  feature?: string;
}

export interface NavSection {
  id: "main" | "academy" | "management" | "account";
  /** Section header (null = no header, main section). */
  header: MessageDescriptor | null;
  items: NavItem[];
}

// ── org role tiers (from lowest to highest operational privilege) ──────────
const TEACHER_TIER: readonly OrgRole[] = ["teacher", "staff", "campus_admin", "org_admin", "owner"];
const STAFF_TIER: readonly OrgRole[] = ["staff", "campus_admin", "org_admin", "owner"];
const CAMPUS_TIER: readonly OrgRole[] = ["campus_admin", "org_admin", "owner"];
const ORG_ADMIN_TIER: readonly OrgRole[] = ["org_admin", "owner"];
const FINANCE_ORG_ROLES: readonly OrgRole[] = ["staff", "org_admin", "owner"];

const inTier = (ctx: NavContext, tier: readonly OrgRole[]) =>
  ctx.orgRole !== null && tier.includes(ctx.orgRole);

/** Teacher tier: own cohort_teacher rows also count (F3-D). */
const teacherTier = (ctx: NavContext) => ctx.isTeacher || inTier(ctx, TEACHER_TIER);

const logged = (ctx: NavContext) => ctx.isLoggedIn;
const everyone = () => true;

export const NAV_SECTIONS: NavSection[] = [
  {
    id: "main",
    header: null,
    items: [
      {
        to: "/learn",
        label: msg`Learn`,
        icon: GraduationCap,
        testid: "nav-learn",
        visible: everyone,
      },
      {
        to: "/catalog",
        label: msg`Catalog`,
        icon: BookOpen,
        testid: "nav-catalog",
        visible: everyone,
      },
      {
        to: "/schedule",
        label: msg`My schedule`,
        icon: CalendarDays,
        testid: "nav-schedule",
        visible: logged,
      },
      {
        to: "/assignments",
        label: msg`My assignments`,
        icon: ClipboardList,
        testid: "nav-assignments",
        visible: logged,
      },
      // 1C · A4: the STUDENT's home. `/academy/cohorts` belongs to the teacher
      // tier, so without this entry whoever's studying had no door to their classroom.
      {
        to: "/classroom",
        label: msg`My classrooms`,
        icon: School,
        testid: "nav-classroom",
        visible: logged,
      },
      // B6 · LEVELS: the student's door to their path. Gated by the visual
      // path's flag (not by the umbrella one): turning it off returns /learn
      // to its usual list without taking anything away from the student.
      {
        to: "/path",
        label: msg`My path`,
        icon: RouteIcon,
        testid: "nav-path",
        feature: "academy.progression.path",
        visible: logged,
      },
      {
        to: "/final-project",
        label: msg`Final project`,
        icon: Trophy,
        testid: "nav-final-project",
        feature: "academy.final_project",
        visible: logged,
      },
      {
        to: "/payments",
        label: msg`My payments`,
        icon: CreditCard,
        testid: "nav-payments",
        feature: "billing.core",
        visible: logged,
      },
      {
        to: "/ai-tutor",
        label: msg`AI Tutor`,
        icon: Sparkles,
        testid: "nav-ai-tutor",
        feature: "academy.ai_tutor",
        visible: logged,
      },
      {
        to: "/leaderboard",
        label: msg`Leagues`,
        icon: Trophy,
        testid: "nav-leaderboard",
        feature: "gamification.core",
        visible: everyone,
      },
    ],
  },
  {
    id: "academy",
    header: msg`Academy`,
    items: [
      {
        to: "/academy",
        label: msg`Dashboard`,
        icon: BarChart3,
        testid: "nav-academy",
        visible: teacherTier,
      },
      {
        to: "/academy/cohorts",
        label: msg`Cohorts`,
        icon: UsersRound,
        testid: "nav-cohorts",
        visible: teacherTier,
      },
      // M3: the tutor's (and any reviewer's) grading queue. Gated by the
      // tutoring vertical; visible to the teacher tier (incl. tutors, who
      // have their own cohort_teacher rows) and admins.
      {
        to: "/academy/tutoring",
        label: msg`Tutoring`,
        icon: ClipboardCheck,
        testid: "nav-tutoring",
        feature: "academy.tutoring",
        visible: teacherTier,
      },
      {
        to: "/academy/people",
        label: msg`People`,
        icon: Users,
        testid: "nav-people",
        visible: (ctx) => inTier(ctx, STAFF_TIER),
      },
      // §L1 · online-first: campuses are IN-PERSON infrastructure. The
      // vertical starts off, so this item isn't shown — but neither the
      // screen nor its data disappear: they come back the moment the flag turns on.
      {
        to: "/academy/campuses",
        label: msg`Campuses`,
        icon: Building2,
        testid: "nav-campuses",
        feature: MODALITY_IN_PERSON_KEY,
        visible: (ctx) => inTier(ctx, CAMPUS_TIER),
      },
      {
        to: "/academy/programs",
        label: msg`Programs`,
        icon: RouteIcon,
        testid: "nav-programs",
        visible: (ctx) => inTier(ctx, ORG_ADMIN_TIER),
      },
      // B1 · entitlement: what happens if a student hasn't paid. Deciding
      // that —and who gets forgiven— is a matter for the academy's owner or
      // administrator, not the front desk: hence ORG_ADMIN_TIER and not STAFF_TIER.
      {
        to: "/academy/entitlement",
        label: msg`Entitlement`,
        icon: ShieldCheck,
        testid: "nav-entitlement",
        feature: "academy.entitlement",
        visible: (ctx) => inTier(ctx, ORG_ADMIN_TIER),
      },
      {
        to: "/academy/announcements",
        label: msg`Announcements`,
        icon: Megaphone,
        testid: "nav-announcements",
        visible: teacherTier,
      },
      // Current authoring rule: any real account can author its own courses
      // (CREATING requires an account; sync enforces author authz).
      {
        to: "/authoring",
        label: msg`Authoring`,
        icon: BookMarked,
        testid: "nav-authoring",
        visible: logged,
      },
    ],
  },
  {
    id: "management",
    header: msg`Management`,
    items: [
      {
        to: "/finance",
        label: msg`Cashier`,
        icon: Banknote,
        testid: "nav-finance",
        feature: "billing.core",
        visible: (ctx) =>
          roleCan(ctx.platformRole, "payments.manage") || inTier(ctx, FINANCE_ORG_ROLES),
      },
      {
        to: "/crm",
        label: msg`CRM`,
        icon: Contact,
        testid: "nav-crm",
        feature: "crm.leads",
        visible: (ctx) => roleCan(ctx.platformRole, "leads.manage") || inTier(ctx, STAFF_TIER),
      },
      {
        // Mirror of the REAL guard: /admin/import-export lives under /admin
        // (isAdminCapable) and the hub lists entities by capability — it only
        // has content for whoever manages data or leads.
        to: "/admin/import-export",
        label: msg`Import / Export`,
        icon: FileSpreadsheet,
        testid: "nav-import-export",
        visible: (ctx) =>
          isAdminCapable(ctx.platformRole) &&
          (roleCan(ctx.platformRole, "data.manage") || roleCan(ctx.platformRole, "leads.manage")),
      },
    ],
  },
  {
    id: "account",
    header: msg`Account`,
    items: [
      {
        to: "/activity",
        label: msg`Activity`,
        icon: History,
        testid: "nav-activity",
        visible: logged,
      },
      { to: "/trash", label: msg`Trash`, icon: Trash2, testid: "nav-trash", visible: logged },
      // 1D · E1: "My record" (progress, grades, attendance, payments and
      // certificates). It used to be a screen only a GUARDIAN reached from the
      // nav, even though its content belongs to the student themself; the
      // ward selector still lives inside, so a guardian enters here just like
      // before and two entries to the same route are no longer needed.
      {
        to: "/profile/academic",
        label: msg`My record`,
        icon: Contact,
        testid: "nav-my-record",
        visible: logged,
      },
      { to: "/profile", label: msg`Profile`, icon: User, testid: "nav-profile", visible: everyone },
      // §M1 · "My profiles": what I am in each academy, what it lets me do and
      // what it doesn't — and WHY (insufficient role vs. feature off). Real
      // account only: a guest has no memberships to explain.
      {
        to: "/profiles",
        label: msg`My profiles`,
        icon: IdCard,
        testid: "nav-profiles",
        visible: logged,
      },
      // AUTONOMY: in-app help center (per-role guides, /help) — visible to
      // everyone: the system's self-description doesn't hide behind a login.
      {
        to: "/help",
        label: msg`Help`,
        icon: LifeBuoy,
        testid: "nav-help",
        visible: everyone,
      },
      // Single entry to the back-office hub: same helper as the /admin guard.
      {
        to: "/admin",
        label: msg`Admin`,
        icon: ShieldCheck,
        testid: "nav-admin",
        visible: (ctx) => isAdminCapable(ctx.platformRole),
      },
    ],
  },
];

/**
 * Items of `section` visible to `ctx`. Returns the SAME references from
 * `NAV_SECTIONS` (a filter, not a map): the sidebar relies on that stable
 * identity so `<For>` reconciles instead of remounting each link when the
 * matrix is re-evaluated.
 */
export function visibleItemsOf(section: NavSection, ctx: NavContext): NavItem[] {
  return section.items.filter(
    (item) => item.visible(ctx) && (item.feature === undefined || ctx.featureEnabled(item.feature)),
  );
}

/** Sections with at least one visible item, already filtered for `ctx`. */
export function visibleSections(
  ctx: NavContext,
): { id: NavSection["id"]; header: MessageDescriptor | null; items: NavItem[] }[] {
  return NAV_SECTIONS.map((section) => ({
    id: section.id,
    header: section.header,
    items: visibleItemsOf(section, ctx),
  })).filter((section) => section.items.length > 0);
}
